import { Component, computed, inject, input, model, signal, effect, DestroyRef } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { AuditEntry, AuditEventType } from '../../../../core/models/audit-entry.model';
import { LoadService } from '../../../../core/services/load.service';
import { UserService } from '../../../../core/services/user.service';
import { AuthService } from '../../../../core/services/auth.service';
import { AssetService } from '../../../../core/services/asset.service';
import { Vehicle } from '../../../../core/models/vehicle.model';
import { TextComponent } from '../../../../shared/ui/typography/text/text.component';
import { BadgeComponent } from '../../../../shared/ui/badge/badge.component';
import { ColorVariant } from '../../../../core/enums/types';
import { ButtonComponent } from '../../../../shared/ui/button/button.component';
import { LucideIconComponent } from '../../../../shared/ui/lucide-icon/lucide-icon.component';
import { CardComponent } from '../../../../shared/ui/layout/card/card.component';
import { Subscription } from 'rxjs';
import { TimeAgoPipe } from '../../../../shared/pipes/time-ago.pipe';

export interface ResolvedAuditUser {
  name: string;
  role: string;
  roleVariant: ColorVariant;
  initials: string;
  isSystem: boolean;
  viaLabel: string;
  viaIcon: string;
}

export interface NodeConfig {
  icon: string;
  bgClass: string;
  textClass: string;
  borderClass: string;
}

@Component({
  selector: 'app-audit-log-timeline',
  standalone: true,
  imports: [
    CommonModule,
    TextComponent,
    BadgeComponent,
    ButtonComponent,
    LucideIconComponent,
    CardComponent,
    TimeAgoPipe,
    DatePipe
  ],
  templateUrl: './audit-log-timeline.component.html',
})
export class AuditLogTimelineComponent {
  loadId = input.required<string>();
  
  private readonly loadService = inject(LoadService);
  private readonly userService = inject(UserService);
  private readonly authService = inject(AuthService);
  private readonly assetService = inject(AssetService);
  private readonly firestore = inject(Firestore);
  private readonly destroyRef = inject(DestroyRef);
  
  private auditSub?: Subscription;
  
  auditEntries = signal<AuditEntry[]>([]);
  collapsed = model<boolean>(true);
  filterType = signal<AuditEventType | 'ALL'>('ALL');
  
  // Cache for resolved profiles fetched from Firestore
  private readonly fetchedUsers = signal<Map<string, { name: string; role?: string }>>(new Map());
  // Cache for resolved vehicles fetched from Firestore
  private readonly fetchedAssets = signal<Map<string, Vehicle>>(new Map());

  filteredEntries = computed(() => {
    const entries = this.auditEntries();
    const filter = this.filterType();
    if (filter === 'ALL') return entries;
    return entries.filter(e => e.eventType === filter);
  });
  
  constructor() {
    effect(() => {
      const id = this.loadId();
      this.auditSub?.unsubscribe();
      if (id) {
        this.auditSub = this.loadService.getAuditLog(id).subscribe(entries => {
          this.auditEntries.set(entries);
          this.preloadEntities(entries);
        });
      } else {
        this.auditEntries.set([]);
      }
    });
    
    this.destroyRef.onDestroy(() => this.auditSub?.unsubscribe());
  }

  private async preloadEntities(entries: AuditEntry[]): Promise<void> {
    const currentUid = this.authService.user?.()?.uid;
    const missingUserUids = new Set<string>();
    const missingAssetIds = new Set<string>();
    const seededUsers = new Map(this.fetchedUsers());
    let seededChanged = false;

    // Cross-seed UID -> name from sibling audit entries on the same load
    for (const entry of entries) {
      if (
        typeof entry.performedByUid === 'string' &&
        entry.performedByUid &&
        typeof entry.performedByName === 'string' &&
        entry.performedByName &&
        entry.performedByName.toLowerCase() !== 'system' &&
        !seededUsers.has(entry.performedByUid)
      ) {
        seededUsers.set(entry.performedByUid, {
          name: entry.performedByName,
          role: typeof entry.userRole === 'string' ? entry.userRole : undefined,
        });
        seededChanged = true;
      }
    }
    if (seededChanged) {
      this.fetchedUsers.set(seededUsers);
    }

    for (const entry of entries) {
      // 1. Check performer
      const rawUser = typeof entry.performedBy === 'string' ? entry.performedBy : '';
      if (rawUser && rawUser.toLowerCase() !== 'system' && rawUser !== currentUid) {
        if (!this.userService?.getUserById?.(rawUser) && !this.fetchedUsers().has(rawUser)) {
          if (rawUser.length >= 20 && !rawUser.includes(' ')) {
            missingUserUids.add(rawUser);
          }
        }
      }

      // 2. Check assignment values for drivers
      if (entry.field === 'driver1Id' || entry.field === 'driver2Id') {
        for (const v of [entry.previousValue, entry.newValue]) {
          if (typeof v === 'string' && v && v !== 'none' && v !== currentUid && !this.userService?.getUserById?.(v) && !this.fetchedUsers().has(v)) {
            if (v.length >= 20 && !v.includes(' ')) {
              missingUserUids.add(v);
            }
          }
        }
      }

      // 3. Check assignment values for trucks/trailers
      if (entry.field === 'truckId' || entry.field === 'trailerId') {
        for (const v of [entry.previousValue, entry.newValue]) {
          if (typeof v === 'string' && v && v !== 'none' && !this.assetService?.getAssetById?.(v) && !this.fetchedAssets().has(v)) {
            if (v.length >= 10) {
              missingAssetIds.add(v);
            }
          }
        }
      }
    }

    // Fetch missing users
    if (missingUserUids.size > 0) {
      const newMap = new Map(this.fetchedUsers());
      for (const uid of missingUserUids) {
        try {
          const userSnap = await getDoc(doc(this.firestore, 'users', uid));
          if (userSnap.exists()) {
            const data = userSnap.data();
            const name = data['fullName'] || data['displayName'] || data['contact']?.email || 'Team Member';
            const role = data['role'] || data['roles']?.[0] || 'admin';
            newMap.set(uid, { name, role });
          } else {
            newMap.set(uid, { name: 'Team Member', role: 'admin' });
          }
        } catch {
          newMap.set(uid, { name: 'Team Member', role: 'admin' });
        }
      }
      this.fetchedUsers.set(newMap);
    }

    // Fetch missing assets
    if (missingAssetIds.size > 0) {
      const newAssetMap = new Map(this.fetchedAssets());
      for (const assetId of missingAssetIds) {
        try {
          const assetSnap = await getDoc(doc(this.firestore, 'assets', assetId));
          if (assetSnap.exists()) {
            newAssetMap.set(assetId, { ...assetSnap.data(), id: assetSnap.id } as Vehicle);
          }
        } catch {
          // ignore
        }
      }
      this.fetchedAssets.set(newAssetMap);
    }
  }

  toAuditDate(value: unknown): Date | null {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    const maybeTs = value as { toDate?: () => Date; seconds?: number };
    if (typeof maybeTs.toDate === 'function') {
      const d = maybeTs.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    }
    if (typeof maybeTs.seconds === 'number') {
      return new Date(maybeTs.seconds * 1000);
    }
    if (typeof value === 'number' || typeof value === 'string') {
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    return null;
  }

  hasFieldDiff(entry: AuditEntry): boolean {
    if (!entry.field) return false;
    const hasPrev = entry.previousValue !== undefined && entry.previousValue !== null && entry.previousValue !== '';
    const hasNext = entry.newValue !== undefined && entry.newValue !== null && entry.newValue !== '';
    return hasPrev || hasNext;
  }

  formatActionTitle(entry: AuditEntry): string {
    switch (entry.eventType) {
      case 'STATUS_CHANGE':
        return 'Status Change';
      case 'ASSIGNMENT_CHANGE':
        if (entry.field === 'truckId') return 'Truck Change';
        if (entry.field === 'trailerId') return 'Trailer Change';
        if (entry.field === 'driver1Id') return 'Primary Driver Change';
        if (entry.field === 'driver2Id') return 'Secondary Driver Change';
        return 'Assignment Change';
      case 'STOP_STATUS_CHANGE':
        if (entry.stopNumber !== undefined && entry.stopNumber !== null) {
          return `Stop #${entry.stopNumber} Status`;
        }
        return 'Stop Status Change';
      case 'FIELD_EDIT':
        if (entry.field) {
          return `${this.formatField(entry.field)} Change`;
        }
        return 'Field Edit';
      case 'DOCUMENT_UPLOAD':
        return 'Document Upload';
      case 'OVERRIDE':
        return 'Override Applied';
      case 'CANCELLATION':
        return 'Load Cancelled';
      case 'SEGMENT_COMPLETION':
        return 'Segment Completed';
      default:
        return 'Load Update';
    }
  }

  formatFieldValue(field: string | undefined, val: any): string {
    if (val === null || val === undefined || val === '' || val === 'none') {
      return 'None';
    }
    const strVal = String(val);

    if (field === 'truckId') {
      const asset = this.assetService?.getAssetById?.(strVal) || this.fetchedAssets().get(strVal);
      if (asset) {
        return asset.unitNumber ? `Truck #${asset.unitNumber}` : (asset.licensePlate ? `Truck (${asset.licensePlate})` : 'Truck');
      }
      return strVal.length > 8 ? `Truck #${strVal.slice(0, 6)}` : `Truck #${strVal}`;
    }

    if (field === 'trailerId') {
      const asset = this.assetService?.getAssetById?.(strVal) || this.fetchedAssets().get(strVal);
      if (asset) {
        return asset.unitNumber ? `Trailer #${asset.unitNumber}` : (asset.licensePlate ? `Trailer (${asset.licensePlate})` : 'Trailer');
      }
      return strVal.length > 8 ? `Trailer #${strVal.slice(0, 6)}` : `Trailer #${strVal}`;
    }

    if (field === 'driver1Id' || field === 'driver2Id') {
      const user = this.userService?.getUserById?.(strVal);
      if (user?.fullName) return user.fullName;
      const cached = this.fetchedUsers().get(strVal);
      if (cached?.name) return cached.name;
      if (this.authService?.user?.()?.uid === strVal) {
        return this.authService.appUser?.()?.fullName || 'You';
      }
      return 'Driver';
    }

    return strVal;
  }

  resolveUser(entry: AuditEntry): ResolvedAuditUser {
    const raw = typeof entry.performedBy === 'string' ? entry.performedBy : '';
    const rawRole = typeof entry.userRole === 'string' ? entry.userRole : '';
    const isExplicitSystem = raw.toLowerCase() === 'system' || (!raw && rawRole.toLowerCase() === 'system');

    // 1. Explicit automated system event
    if (isExplicitSystem && (!entry.performedByName || String(entry.performedByName).toLowerCase() === 'system')) {
      return {
        name: 'System Automated',
        role: 'System',
        roleVariant: 'secondary',
        initials: 'SY',
        isSystem: true,
        viaLabel: this.formatVia(entry.performedVia),
        viaIcon: this.getViaIcon(entry.performedVia)
      };
    }

    // 2. Human user resolution
    let name = typeof entry.performedByName === 'string' ? entry.performedByName : '';
    let role = rawRole && rawRole.toLowerCase() !== 'system' ? rawRole : '';
    const currentUid = this.authService?.user?.()?.uid;

    if (!name) {
      if (raw === currentUid) {
        name = this.authService.appUser?.()?.fullName || this.authService.user?.()?.displayName || 'You';
        if (!role) role = this.authService.activeCompanyRoles?.()?.[0] || 'Admin';
      } else {
        const localUser = this.userService?.getUserById?.(raw);
        if (localUser) {
          name = localUser.fullName || localUser.contact?.email || 'Team Member';
          if (!role) role = localUser.currentCompanyRoles?.[0] || 'Admin';
        } else {
          const cached = this.fetchedUsers().get(raw);
          if (cached) {
            name = cached.name;
            if (!role) role = cached.role || 'Admin';
          } else if (raw.length >= 20 && !raw.includes(' ')) {
            name = 'Team Member';
            if (!role) role = 'Admin';
          } else if (raw) {
            name = raw;
          } else {
            name = 'Team Member';
          }
        }
      }
    }

    if (!role || role.toLowerCase() === 'system') {
      role = 'Admin';
    }

    const formattedRole = this.formatRole(role);
    const roleVariant = this.getRoleBadgeVariant(formattedRole);
    const initials = this.getInitials(name);

    return {
      name,
      role: formattedRole,
      roleVariant,
      initials,
      isSystem: false,
      viaLabel: this.formatVia(entry.performedVia),
      viaIcon: this.getViaIcon(entry.performedVia)
    };
  }

  formatRole(role: string): string {
    const map: Record<string, string> = {
      admin: 'Admin',
      root: 'Admin',
      dispatch_manager: 'Dispatch Manager',
      assets_manager: 'Assets Manager',
      dispatcher: 'Dispatcher',
      driver: 'Driver',
      accounting: 'Accounting',
      safety: 'Safety',
      maintenance: 'Maintenance',
      owner_operator: 'Owner Operator',
      carrier: 'Carrier',
      system: 'System'
    };
    return map[role.toLowerCase()] || (role.charAt(0).toUpperCase() + role.slice(1));
  }

  getRoleBadgeVariant(role: string): ColorVariant {
    switch (role.toLowerCase()) {
      case 'admin':
      case 'root':
        return 'primary';
      case 'dispatch_manager':
      case 'dispatch manager':
      case 'assets_manager':
      case 'assets manager':
      case 'dispatcher':
        return 'info';
      case 'accounting':
        return 'success';
      case 'driver':
      case 'owner operator':
        return 'warning';
      default:
        return 'secondary';
    }
  }

  getInitials(name: string): string {
    if (!name) return 'U';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }

  formatVia(via?: string): string {
    switch (via) {
      case 'admin_app': return 'Web Admin';
      case 'driver_app': return 'Driver App';
      case 'dispatch_app': return 'Dispatcher';
      case 'cloud_function': return 'Automation';
      case 'api': return 'API';
      default: return 'Web Admin';
    }
  }

  getViaIcon(via?: string): string {
    switch (via) {
      case 'admin_app': return 'AppWindow';
      case 'driver_app': return 'Smartphone';
      case 'dispatch_app': return 'Radio';
      case 'cloud_function': return 'Zap';
      default: return 'AppWindow';
    }
  }

  isMeaningfulReason(reason?: string): boolean {
    if (!reason) return false;
    const trimmed = reason.trim().toLowerCase();
    return (
      trimmed !== 'status update' &&
      trimmed !== 'stop status update' &&
      trimmed !== 'status updated' &&
      trimmed !== 'updated' &&
      trimmed !== 'reassigned' &&
      trimmed.length > 0
    );
  }

  formatField(field?: string): string {
    if (!field) return '';
    if (field === 'status') return 'Status';
    if (field === 'driver1Id') return 'Primary Driver';
    if (field === 'driver2Id') return 'Secondary Driver';
    if (field === 'truckId') return 'Truck';
    if (field === 'trailerId') return 'Trailer';
    if (field.includes('stops')) return 'Stop Status';
    return field.charAt(0).toUpperCase() + field.slice(1);
  }

  getStatusBadgeVariant(status?: unknown): ColorVariant {
    if (status === null || status === undefined || status === '') return 'secondary';
    switch (String(status).toUpperCase()) {
      case 'VERIFIED': return 'primary';
      case 'DISPATCHED':
      case 'ACTIVE':
      case 'IN_TRANSIT':
      case 'AT_STOP': return 'info';
      case 'DELIVERED':
      case 'COMPLETED': return 'success';
      case 'CANCELLED': return 'danger';
      case 'ACTION_REQUIRED':
      case 'DRAFT': return 'warning';
      default: return 'secondary';
    }
  }

  getNodeConfig(eventType: AuditEventType): NodeConfig {
    switch (eventType) {
      case 'STATUS_CHANGE':
        return { icon: 'ArrowRight', bgClass: 'bg-primary/10', textClass: 'text-primary', borderClass: 'border-primary/30' };
      case 'STOP_STATUS_CHANGE':
        return { icon: 'MapPin', bgClass: 'bg-info/10', textClass: 'text-info', borderClass: 'border-info/30' };
      case 'ASSIGNMENT_CHANGE':
        return { icon: 'UserCheck', bgClass: 'bg-primary/10', textClass: 'text-primary', borderClass: 'border-primary/30' };
      case 'OVERRIDE':
        return { icon: 'ShieldAlert', bgClass: 'bg-warning/10', textClass: 'text-warning', borderClass: 'border-warning/30' };
      case 'CANCELLATION':
        return { icon: 'XCircle', bgClass: 'bg-danger/10', textClass: 'text-danger', borderClass: 'border-danger/30' };
      case 'DOCUMENT_UPLOAD':
        return { icon: 'Upload', bgClass: 'bg-secondary', textClass: 'text-foreground', borderClass: 'border-border' };
      case 'FIELD_EDIT':
        return { icon: 'Pencil', bgClass: 'bg-secondary', textClass: 'text-foreground', borderClass: 'border-border' };
      case 'SEGMENT_COMPLETION':
        return { icon: 'CheckCircle', bgClass: 'bg-success/10', textClass: 'text-success', borderClass: 'border-success/30' };
      default:
        return { icon: 'History', bgClass: 'bg-surface-high', textClass: 'text-muted', borderClass: 'border-border' };
    }
  }
}
